import { useState, useCallback, useMemo } from "react";
import { useAppContext } from "../../../../AppContext";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { IUserConsentViewModel } from "../models/userConsentViewModel";

export const useMyConsents = () => {
  const { isDesktopOrTablet, selectedStudio } = useAppContext();
  const [selectedConsentId, setSelectedConsentId] = useState<number | undefined>();

  const {
    data: consents,
    isLoading,
  } = useApiQuery<IUserConsentViewModel[]>({
    queryKey: [endpointsMetadata.myConsents.getAll],
    endpoint: endpointsMetadata.myConsents.getAll,
    enabled: selectedStudio && selectedStudio?.id > 0,
    globalIsLoading: false,
    disableErrorToast: false,
  });

  // Group consents into unsigned (both required and optional), current, and past
  const unsignedConsents = useMemo(
    () => consents?.filter((c) => c.givenAt === null) ?? [],
    [consents]
  );

  const currentConsents = useMemo(
    () => consents?.filter((c) => c.isCurrent && c.givenAt !== null) ?? [],
    [consents]
  );

  const pastConsents = useMemo(
    () => consents?.filter((c) => !c.isCurrent && c.givenAt !== null) ?? [],
    [consents]
  );

  const selectedConsent = useMemo(
    () => consents?.find((c) => c.consentId === selectedConsentId),
    [consents, selectedConsentId]
  );

  const handleOpenDialog = useCallback((consentId: number) => {
    setSelectedConsentId(consentId);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedConsentId(undefined);
  }, []);

  return {
    unsignedConsents,
    currentConsents,
    pastConsents,
    isLoading,
    selectedConsent,
    isDesktopOrTablet,
    handleOpenDialog,
    handleCloseDialog,
  };
};
